/**
 * weekConfig.js
 *
 * Client-side mirror of `server/services/weekConfigService.js`.
 *
 * Any component that decides whether a date is a weekoff / half-day - date
 * pickers, leave-day counting, notice-period math - MUST use these helpers
 * instead of hardcoding Saturday(6)/Sunday(0). Hardcoding silently breaks
 * every company not on a standard 5-day Sat+Sun week.
 *
 * Keep this file in sync with the server service; the rules below are a
 * one-to-one port of `isWeekoff` / `isHalfDay`.
 *
 * All functions accept a moment (or dayjs) instance - anything exposing
 * `.day()` (0=Sun … 6=Sat) and `.date()` (day-of-month).
 */

/**
 * Default grid: Saturday + Sunday off, every week of the month (5 rows, one per
 * week-of-month; each row is indexed by day of week 0=Sun ... 6=Sat).
 */
const DEFAULT_ROW = ["off", "work", "work", "work", "work", "work", "off"];
export const DEFAULT_WEEK_CONFIG = {
  pattern: [0, 1, 2, 3, 4].map(() => [...DEFAULT_ROW]),
  halfDaySide: "second",
  source: "default",
};

const toYmd = (d) => {
  if (!d) return null;
  if (typeof d === "string") return d.slice(0, 10);
  if (typeof d.format === "function") return d.format("YYYY-MM-DD");
  if (d instanceof Date) {
    const m = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${m(d.getMonth() + 1)}-${m(d.getDate())}`;
  }
  return null;
};

/**
 * Latest workweek timeline entry with effectiveFrom <= the date (plain string
 * compare). Returns the entry's pattern grid, or null when none applies.
 */
export const pickWorkweekPattern = (timeline, dateOrYmd) => {
  if (!Array.isArray(timeline) || timeline.length === 0) return null;
  const ymd = toYmd(dateOrYmd);
  if (!ymd) return null;
  let picked = null;
  timeline.forEach((entry) => {
    if (
      entry?.effectiveFrom &&
      String(entry.effectiveFrom).slice(0, 10) <= ymd &&
      (!picked || String(entry.effectiveFrom) >= String(picked.effectiveFrom))
    ) {
      picked = entry;
    }
  });
  return picked?.pattern || null;
};

const pickWorkweekEntry = (timeline, dateOrYmd) => {
  if (!Array.isArray(timeline) || timeline.length === 0) return null;
  const ymd = toYmd(dateOrYmd);
  if (!ymd) return null;
  let picked = null;
  timeline.forEach((entry) => {
    if (
      entry?.effectiveFrom &&
      String(entry.effectiveFrom).slice(0, 10) <= ymd &&
      (!picked || String(entry.effectiveFrom) >= String(picked.effectiveFrom))
    ) {
      picked = entry;
    }
  });
  return picked;
};

/**
 * Grid config `{pattern, source:'workweek'}` for the date from the timeline.
 * `shiftWeekConfig` is ignored except as a last resort when the timeline is
 * empty or missing, and only if it is itself a grid; otherwise the default grid.
 */
export const resolveWeekConfigForDate = (
  shiftWeekConfig,
  workweekTimeline,
  date
) => {
  const entry = pickWorkweekEntry(workweekTimeline, date);
  if (entry?.pattern) {
    return {
      pattern: entry.pattern,
      halfDaySide: entry.halfDaySide === "first" ? "first" : "second",
      source: "workweek",
    };
  }
  if (
    (!Array.isArray(workweekTimeline) || workweekTimeline.length === 0) &&
    hasGrid(shiftWeekConfig)
  ) {
    return shiftWeekConfig;
  }
  return DEFAULT_WEEK_CONFIG;
};

const hasGrid = (config) => Array.isArray(config?.pattern);

const gridOf = (config) => (hasGrid(config) ? config : DEFAULT_WEEK_CONFIG);

const getGridCell = (date, config) => {
  const week = Math.min(5, Math.max(1, Math.ceil(date.date() / 7)));
  return gridOf(config).pattern[week - 1]?.[date.day()];
};

/** Which occurrence of its weekday this date is within its month (1–5). */
export const getWeekNumberOfMonth = (date) => Math.ceil(date.date() / 7);

/**
 * Whether the grid has ANY weekoff cell at all. False for a 7-day working week.
 * UI that offers an "include weekoffs" choice should hide it in that case.
 *
 * @param {object|null} config - grid config (anything else is the default grid)
 * @returns {boolean}
 */
export const hasWeekoffDays = (config) =>
  gridOf(config).pattern.some(
    (row) => Array.isArray(row) && row.some((c) => c === "off")
  );

/**
 * Whether the given date is a full weekoff in the grid.
 *
 * @param {object} date - moment/dayjs instance
 * @param {object} config - grid config, or DEFAULT_WEEK_CONFIG
 * @returns {boolean}
 */
export const isWeekoffDay = (date, config = DEFAULT_WEEK_CONFIG) => {
  if (!date) return false;
  return getGridCell(date, config) === "off";
};

/**
 * Walks backward from `date` to the nearest day that is neither a weekoff (per
 * the grid) nor a public holiday. Returns `date` itself when it already is
 * a working day.
 *
 * Used for notice-period / last-working-date math, which must land on a real
 * working day for the employee's own shift - not on a hardcoded Mon–Fri.
 *
 * @param {object} date - moment/dayjs instance (not mutated)
 * @param {object} weekConfig - grid config, or DEFAULT_WEEK_CONFIG
 * @param {string[]} holidayDates - 'YYYY-MM-DD' strings
 * @returns {object} a new moment/dayjs instance
 */
export const lastWorkingDayOnOrBefore = (
  date,
  weekConfig = DEFAULT_WEEK_CONFIG,
  holidayDates = []
) => {
  const holidays = Array.isArray(holidayDates) ? holidayDates : [];
  const cursor = date.clone();
  // Bounded so a misconfigured all-weekoff config cannot hang the UI.
  const MAX_STEPS = 366;
  let steps = 0;
  while (
    steps < MAX_STEPS &&
    (isWeekoffDay(cursor, weekConfig) ||
      holidays.includes(cursor.format("YYYY-MM-DD")))
  ) {
    cursor.subtract(1, "days");
    steps++;
  }
  return cursor;
};

/**
 * Whether the given date is a half working day in the grid.
 *
 * @param {object} date - moment/dayjs instance
 * @param {object} config - grid config, or DEFAULT_WEEK_CONFIG
 * @returns {boolean}
 */
export const isHalfWorkingDay = (date, config = DEFAULT_WEEK_CONFIG) => {
  if (!date) return false;
  return getGridCell(date, config) === "half";
};

/**
 * Whether the given date is a half week off day ('half' cell of the grid).
 * No leave can be applied on such a day.
 *
 * @param {object} date - moment/dayjs instance
 * @param {object} config - grid config, or DEFAULT_WEEK_CONFIG
 * @returns {boolean}
 */
export const isHalfWeekoffDay = (date, config = DEFAULT_WEEK_CONFIG) =>
  isHalfWorkingDay(date, config);

/**
 * Which half is off / working on a half week off day, or null otherwise.
 * Mirrors `getHalfWeekoffInfo` in server/services/weekConfigService.js.
 *
 * @param {object} date - moment/dayjs instance
 * @param {object} config - grid config (halfDaySide 'first' | 'second')
 * @returns {{offHalf: string, workingHalf: string}|null}
 */
export const getHalfWeekoffInfo = (date, config = DEFAULT_WEEK_CONFIG) => {
  if (!date || getGridCell(date, config) !== "half") return null;
  return config && config.halfDaySide === "first"
    ? { offHalf: "First Half", workingHalf: "Second Half" }
    : { offHalf: "Second Half", workingHalf: "First Half" };
};
